// DEPENDENCIES
import { FormikValues } from "formik";
import * as Sentry from "@sentry/react";

// TYPES
import { AppDispatch } from "../../app/store";
import { InputType, Step } from "../../types";

// UTILS
import { onUpdateInfoStep } from "../../reducers/infoStepReducer";
import { onUpdateStep } from "../../reducers/onboardingDataReducer";
import {
  asCuit,
  getCompanyData,
  getDniFromCuit,
  getPartnerCompanyType,
  isAllariaMasOnboarding,
  isAllariaOnboarding,
  isJuridicalCuit,
  isUnifiedOnboarding,
  yesOrNoBoolOptions,
} from "../../utils";
import { authEmail } from "../../connectors/connectors";
import { updateSignatureRequestId } from "../../reducers/authReducer";
import { logAnalyticsAndHotjarEvent } from "../../main";
import {
  cuitRequired,
  emailRequired,
  radioOrSingleSelectRequired,
  smallStringRequired,
} from "../../validations";
import { isDev } from "../../app/utils";

// COMPONENTS
import UnificationCard from "../../components/UnificationCard/UnificationCard";

const basicInformationStep = (): Omit<Step, "step"> => ({
  name: "Cuenta personal",
  titleOfStep: "Información básica",
  disableBackNavigation: (
    _: FormikValues,
    nextStepStatus: "completed" | "disabled"
  ) => nextStepStatus === "completed",
  fields: [
    {
      initialValue: "",
      type: "cuit",
      name: "cuit",
      label: "CUIT / CUIL",
      placeholder: "XX-XXXXXXXX-X",
      validation: cuitRequired,
      formatInPreConfirmation: (value) => asCuit(value),
      ...(isDev ? { disabledByUrlParams: ["cuit", "accountCuit"] } : {}),
    },
    {
      initialValue: "",
      type: "email",
      name: "email",
      label: "Mail",
      validation: emailRequired,
      placeholder: "direccion@mail.com",
      disabledByUrlParams: isDev ? ["email", "accountEmail"] : "email",
    },
    {
      name: "referredBool",
      type: "radio",
      label: "¿Fuiste referido?",
      validation: radioOrSingleSelectRequired(yesOrNoBoolOptions),
      initialValue: "",
      options: yesOrNoBoolOptions,
      disabledByUrlParams: "ref",
    },
    {
      conditional: {
        name: "referredBool",
        value: "SI",
      },
      initialValue: "",
      type: "text",
      name: "referrer",
      label: "Nombre o mail de quien te refirió",
      validation: smallStringRequired,
      disabledByUrlParams: "ref",
    },
    {
      name: "labelReferrer",
      type: "label" as InputType,
      label: "",
      initialValue: "",
      text: <BlanqueoAlert />,
      hideField: !isAllariaOnboarding(),
    },
    {
      name: "unifiedLabel",
      type: "label" as InputType,
      label: "",
      initialValue: "",
      text: <UnificationCard />,
      hideField: !(isUnifiedOnboarding() && isAllariaMasOnboarding()),
    },
  ],
  onContinue: async (values: FormikValues, dispatch) => {
    if (!dispatch || !values) throw new Error("Error: dispatch not found");

    //To prevent failed loop
    dispatch(onUpdateInfoStep(null));

    const { cuit } = values;

    return isJuridicalCuit(cuit)
      ? handleInvalidCuit(dispatch)
      : createOnboarding(dispatch, values);
  },
});

const handleInvalidCuit = (dispatch: AppDispatch) => {
  dispatch(onUpdateInfoStep(null));
  dispatch(onUpdateStep("information-step"));
  throw new Error("CUIT no válido");
};

const createOnboarding = async (
  dispatch: AppDispatch,
  values: FormikValues
) => {
  const { cuit, email } = values;
  const partnerCompanyType = getPartnerCompanyType(values);

  const res = await authEmail({
    email,
    companyType: getCompanyData().companyName,
    ...(partnerCompanyType ? { partnerCompanyType } : {}),
  });

  dispatch(updateSignatureRequestId(res.data.signatureRequestId));
  logAnalyticsAndHotjarEvent("step_information_basic_completed_pf");

  if (email) Sentry.setUser({ email });

  return { newValuesToForm: { identityDocument: getDniFromCuit(cuit) } };
};

const BlanqueoAlert = () => (
  <p
    style={{
      color: "#001C40",
      fontSize: "0.7025rem",
      fontWeight: 400,
    }}
  >
    Si estás solicitando la apertura de tu cuenta comitente para adherir al{" "}
    <a
      style={{
        color: "#0150B7",
        fontWeight: 600,
        textDecorationLine: "underline",
      }}
      href="https://www.argentina.gob.ar/normativa/nacional/ley-27743-401268/normas-modifican"
      target="_blank"
      rel="noopener noreferrer"
    >
      blanqueo (ley 27.743)
    </a>
    , seleccioná "<strong>Sí</strong>" en "<strong>¿Fuiste referido?</strong>" y
    luego indicá "<strong>cuenta blanqueo</strong>" en el campo de texto.
  </p>
);

export default basicInformationStep;
